import React from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import EMPButton from "../../../shared/components/EMPButton/EMPButton";
import MotionChevronRightCircle from "../../../shared/Icons/MotionChevronRightCircle";
import OnboardingPageTitle from "../../../shared/components/OnboardingPageTitle/OnboardingPageTitle";

const ACCESSORY_PRICING_LINK = "/pricing/accessories";
const ACCESSORY_PRICING_COLOR = "#485B65";
const ACCESSORY_PRICING_TRANSLATION_KEY =
  "DealerOnboarding.content.accessoryPricing";

const OnboardingAccessoryPricing = () => {
  const { t } = useTranslation();

  return (
    <div className="tw-bg-white tw-rounded-lg tw-p-8">
      <OnboardingPageTitle testId="accessory-pricing-title" className="tw-mb-6">
        {t(`${ACCESSORY_PRICING_TRANSLATION_KEY}.title`, "Accessory Pricing")}
      </OnboardingPageTitle>

      <div className="tw-mb-6">
        <EMPButton
          variant="outlined"
          hasRightIcon
          rightIcon={<MotionChevronRightCircle />}
          testId="accessory-pricing-checklist-button"
          sx={{
            minWidth: "auto",
            height: "36px",
            borderRadius: "0px !important",
            padding: "6px 16px",
            color: ACCESSORY_PRICING_COLOR,
            borderColor: `${ACCESSORY_PRICING_COLOR} !important`,
            backgroundColor: "#FFFFFF",
            textTransform: "none",
            fontFamily:
              'var(--body-2-bold-font-family, "Lincoln Proxima Nova")',
            fontSize: "var(--body-2-bold-font-size, 14px)",
            fontStyle: "normal",
            fontWeight: "var(--body-2-bold-font-weight, 700)",
            lineHeight: "var(--body-2-bold-line-height, 20px)",
            boxShadow: "none",
            "&:hover": {
              color: ACCESSORY_PRICING_COLOR,
              backgroundColor: "rgba(72, 91, 101, 0.04)",
              borderColor: `${ACCESSORY_PRICING_COLOR} !important`,
              boxShadow: "none",
            },
            "& .MuiButton-endIcon": {
              color: ACCESSORY_PRICING_COLOR,
            },
            "& svg": {
              color: ACCESSORY_PRICING_COLOR,
              fill: ACCESSORY_PRICING_COLOR,
            },
            "& path": {
              fill: ACCESSORY_PRICING_COLOR,
            },
          }}
        >
          {t(
            `${ACCESSORY_PRICING_TRANSLATION_KEY}.checklistButtonLabel`,
            "Accessory Pricing Checklist"
          )}
        </EMPButton>
      </div>

      <Typography
        component="p"
        data-testid="accessory-pricing-description"
        sx={{
          color: "var(--color-text-onlight-moderate-default, #333)",
          fontFamily:
            'var(--body-1-regular-font-family, "Lincoln Proxima Nova")',
          fontSize: "var(--body-1-regular-font-size, 16px)",
          fontStyle: "normal",
          fontWeight: "var(--body-1-regular-font-weight, 400)",
          lineHeight: "var(--body-1-regular-line-height, 24px)",
          margin: 0,
          marginBottom: "24px",
          maxWidth: "720px",
        }}
      >
        {t(
          `${ACCESSORY_PRICING_TRANSLATION_KEY}.description`,
          "Pricing in the eCommerce Marketplace Portal (EMP) provides dealers the opportunity to set the pricing structure for accessories and labor rates for installation when it becomes available on Lincoln.com."
        )}
      </Typography>

      <Box
        className="tw-mb-6"
        data-testid="accessory-pricing-banner"
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: "12px",
          backgroundColor: "#F5F6F7",
          borderRadius: "8px",
          padding: "16px 20px",
          maxWidth: "720px",
        }}
      >
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            minWidth: "22px",
            width: "22px",
            height: "22px",
            borderRadius: "50%",
            border: `1.5px solid ${ACCESSORY_PRICING_COLOR}`,
            color: ACCESSORY_PRICING_COLOR,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily:
              'var(--body-2-bold-font-family, "Lincoln Proxima Nova")',
            fontSize: "13px",
            fontStyle: "normal",
            fontWeight: 700,
            lineHeight: "13px",
            marginTop: "1px",
          }}
        >
          i
        </Box>

        <Box>
          <Typography
            component="p"
            data-testid="accessory-pricing-banner-title"
            sx={{
              color: "#1a1a1a",
              fontFamily:
                'var(--body-2-bold-font-family, "Lincoln Proxima Nova")',
              fontSize: "var(--body-2-bold-font-size, 14px)",
              fontStyle: "normal",
              fontWeight: "var(--body-2-bold-font-weight, 700)",
              lineHeight: "var(--body-2-bold-line-height, 20px)",
              margin: 0,
            }}
          >
            {t(
              `${ACCESSORY_PRICING_TRANSLATION_KEY}.bannerTitle`,
              "Placeholder banner title"
            )}
          </Typography>

          <Typography
            component="p"
            data-testid="accessory-pricing-banner-body"
            sx={{
              color: "var(--color-text-onlight-moderate-default, #333)",
              fontFamily:
                'var(--body-1-regular-font-family, "Lincoln Proxima Nova")',
              fontSize: "var(--body-1-regular-font-size, 16px)",
              fontStyle: "normal",
              fontWeight: "var(--body-1-regular-font-weight, 400)",
              lineHeight: "var(--body-1-regular-line-height, 24px)",
              margin: 0,
            }}
          >
            {t(
              `${ACCESSORY_PRICING_TRANSLATION_KEY}.bannerBody`,
              "Placeholder banner message"
            )}
          </Typography>
        </Box>
      </Box>

      <EMPButton
        variant="contained"
        href={ACCESSORY_PRICING_LINK}
        hasRightIcon
        rightIcon={<MotionChevronRightCircle />}
        testId="set-accessory-pricing-button"
        sx={{
          minWidth: "auto",
          height: "40px",
          borderRadius: "0px !important",
          padding: "8px 18px",
          color: "var(--button-primary-onlight-content, #FFF)",
          textAlign: "center",
          fontFamily: 'var(--body-2-bold-font-family, "Lincoln Proxima Nova")',
          fontSize: "var(--body-2-bold-font-size, 14px)",
          fontStyle: "normal",
          fontWeight: "var(--body-2-bold-font-weight, 700)",
          lineHeight: "var(--body-2-bold-line-height, 20px)",
          textTransform: "none",
          backgroundColor: `${ACCESSORY_PRICING_COLOR} !important`,
          borderColor: `${ACCESSORY_PRICING_COLOR} !important`,
          boxShadow: "none",
          "&:hover": {
            color: "var(--button-primary-onlight-content, #FFF)",
            backgroundColor: `${ACCESSORY_PRICING_COLOR} !important`,
            borderColor: `${ACCESSORY_PRICING_COLOR} !important`,
            boxShadow: "none",
          },
          "& .MuiButton-endIcon": {
            color: "var(--button-primary-onlight-content, #FFF)",
          },
          "& svg": {
            color: "var(--button-primary-onlight-content, #FFF)",
            fill: "var(--button-primary-onlight-content, #FFF)",
          },
          "& path": {
            fill: "var(--button-primary-onlight-content, #FFF)",
          },
        }}
      >
        {t(
          `${ACCESSORY_PRICING_TRANSLATION_KEY}.linkButtonLabel`,
          "Set Accessory Pricing"
        )}
      </EMPButton>
    </div>
  );
};

export default OnboardingAccessoryPricing;
